import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, finalize, of, tap } from 'rxjs';
import { PresenceDTO } from 'src/app/modules/agenda/components/models/presence.model';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { SpinnerServiceService } from 'src/app/modules/shared/serviceSpinner/spinner-service.service';

export type AttendanceState =
  | 'READY_TO_VALIDATE'
  | 'PRESENT_CONFIRMED'
  | 'ALREADY_PRESENT'
  | 'NOT_CONFIRMED'
  | 'ERROR';

export interface AttendanceResponse {
  state: AttendanceState;
  activityName: string | null;
  activityDate: string | null;
  activityHours: string | null;
  activityAddress: string | null;
  beneficiaryFirstName: string | null;
  beneficiaryLastName: string | null;
  beneficiaryEmail: string | null;
  beneficiaryWaveNumber: string | null;
  errorCode: string | null;
}
type ViewState =
  | 'loading'
  | 'form'
  | 'already-present'
  | 'not-confirmed'
  | 'confirmed'
  | 'code-expired'
  | 'error';
@Component({
  selector: 'app-activity-attendance-confirm',
  templateUrl: './activity-attendance.component.html',
  styleUrls: ['./activity-attendance.component.css'],
})
export class ActivityAttendanceComponent implements OnInit {
  confirmationForm!: FormGroup;

  idActivity!: number;
  iuf!: string;
  filled: boolean = false;
  codeMatched: boolean = true;

  viewState: ViewState = 'loading';
  data: AttendanceResponse | null = null;
  initialWaveNumber: string | null = null;

  showSpinner = false;
  isSubmitting: boolean = false;
  confirmOnSite = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private spinnerService: SpinnerServiceService,
    private service: AuthService,
  ) {
    this.idActivity = +this.route.snapshot.params['idActivity'];
    this.iuf = this.route.snapshot.params['iuf'];
  }

  ngOnInit(): void {
    this.initForm();
    this.getData();
  }

  initForm() {
    this.confirmationForm = this.formBuilder.group({
      prenom: ['', Validators.required],
      nom: ['', Validators.required],
      email: ['', Validators.required],
      waveNumber: ['', Validators.required],
      pin1: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      pin2: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      pin3: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      pin4: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
    });
  }

  isFieldInvalid(controlName: string): boolean | null {
    const control = this.confirmationForm.get(controlName);
    return control && control.invalid && control.touched;
  }

  getData() {
    this.viewState = 'loading';
    this.spinnerService.showSpinner();

    this.service
      .getAttendanceStatus(this.iuf, this.idActivity)
      .pipe(
        tap((response: AttendanceResponse) => {
          this.data = response;
          this.viewState = this.mapState(response.state);

          if (this.viewState === 'form') {
            this.updateForm(response);
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.viewState = 'error';
          this.data = (err.error as AttendanceResponse) ?? null;
          return EMPTY;
        }),
        finalize(() => {
          this.spinnerService.hideSpinner();
        }),
      )
      .subscribe();
  }

  private mapState(state: AttendanceState): ViewState {
    switch (state) {
      case 'READY_TO_VALIDATE':
        return 'form';
      case 'ALREADY_PRESENT':
        return 'already-present';
      case 'NOT_CONFIRMED':
        return 'not-confirmed';
      default:
        return 'error';
    }
  }

  updateForm(data: AttendanceResponse) {
    this.initialWaveNumber = data.beneficiaryWaveNumber;

    this.confirmationForm.patchValue({
      prenom: data.beneficiaryFirstName,
      nom: data.beneficiaryLastName,
      email: data.beneficiaryEmail,
      waveNumber: data.beneficiaryWaveNumber,
    });

    this.confirmationForm.controls['prenom'].disable();
    this.confirmationForm.controls['nom'].disable();
    this.confirmationForm.controls['email'].disable();
    // waveNumber reste modifiable volontairement
  }

  onSubmitForm() {
    this.isSubmitting = true;

    if (this.confirmationForm.valid) {
      this.filled = false;
      const formValue = this.confirmationForm.value;
      const code = `${formValue['pin1']}${formValue['pin2']}${formValue['pin3']}${formValue['pin4']}`;
      this.codeMatched = true;

      this.setPresence(
        this.idActivity,
        this.iuf,
        code,
        formValue['waveNumber'],
      );
    } else {
      this.filled = true;
    }

    this.isSubmitting = false;
  }

  public setPresence(
    activityId: number,
    iuf: string,
    securityCode: string,
    waveNumber: string,
  ) {
    this.showSpinner = true;

    const waveNumberChanged = waveNumber !== this.initialWaveNumber;

    this.service
      .validateAttendance(
        iuf,
        activityId,
        securityCode,
        waveNumberChanged ? waveNumber : null,
      )
      .pipe(
        tap((response: AttendanceResponse) => {
          this.codeMatched = true;
          this.data = response;
          this.viewState = this.mapConfirmState(response.state);
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401) {
            this.codeMatched = false; // code incorrect : on reste sur le formulaire
          } else if (err.status === 410) {
            this.viewState = 'code-expired'; // activité terminée ou aucun code actif
          } else {
            this.viewState = 'error';
            this.data = (err.error as AttendanceResponse) ?? this.data;
          }
          return EMPTY;
        }),
        finalize(() => {
          this.showSpinner = false;
        }),
      )
      .subscribe();
  }

  private mapConfirmState(state: AttendanceState): ViewState {
    switch (state) {
      case 'PRESENT_CONFIRMED':
        return 'confirmed';
      case 'ALREADY_PRESENT':
        return 'already-present';
      case 'NOT_CONFIRMED':
        return 'not-confirmed';
      default:
        return 'error';
    }
  }

  autoFocus(event: any, next: string) {
    if (event.target.value && next) {
      const el = document.querySelector(
        `[formControlName="${next}"]`,
      ) as HTMLElement;
      el?.focus();
    }
  }

  onlyNumber(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  openFormFromNotConfirmed() {
    if (!this.data) return;
    this.confirmOnSite = true;
    this.updateForm(this.data);
    this.viewState = 'form';
  }
}
